import { useRef, useState } from 'react';
import { Box, Button, Stack, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  ON_FIELD_BALL_DECISION_LABELS,
  ON_FIELD_BALL_DECISION_OPTIONS,
  ON_FIELD_PUNISHMENT_LABELS,
  ON_FIELD_PUNISHMENT_OPTIONS,
  REVIEWED_BALL_DECISION_LABELS,
  REVIEWED_BALL_DECISION_OPTIONS,
  REVIEWED_PUNISHMENT_LABELS,
  REVIEWED_PUNISHMENT_OPTIONS,
  JUDGEMENT_OPTIONS,
  JUDGEMENT_LABELS,
  JUDGEMENT_UNCERTAIN_COLOR,
  getJudgementTextColor,
  type JudgementOption,
} from '@/types';
import RemarkQuickInsertDialog, { appendRemarkQuickInsert } from '@/components/review/RemarkQuickInsertDialog';

export interface DecisionFormValues {
  on_field_decision: string;
  on_field_punishment: string;
  reviewed_decision: string;
  reviewed_punishment: string;
  judgement: string;
  note: string;
  team_side: string;
  event_type: string;
}

export const emptyDecisionForm = (): DecisionFormValues => ({
  on_field_decision: '',
  on_field_punishment: 'no_penalty',
  reviewed_decision: '',
  reviewed_punishment: '',
  judgement: '',
  note: '',
  team_side: '',
  event_type: 'decision',
});

interface Props {
  values: DecisionFormValues;
  onChange: (field: keyof DecisionFormValues, value: string) => void;
  showJudgement?: boolean;
  compact?: boolean;
  dark?: boolean;
  /** Raise select menus above fullscreen overlays (e.g. pro mode at z-index 2000). */
  menuZIndex?: number;
  /** Raise remark quick-insert dialog above fullscreen overlays. */
  dialogZIndex?: number;
}

function buildSelectMenuProps(dark: boolean, menuZIndex?: number) {
  const zIndex = menuZIndex ?? (dark ? 1500 : undefined);
  if (zIndex == null) return undefined;

  return {
    MenuProps: {
      disableScrollLock: true,
      sx: { zIndex },
      PaperProps: {
        sx: {
          maxHeight: 320,
          ...(dark
            ? {
                bgcolor: '#2a2a2a',
                color: '#fff',
                '& .MuiMenuItem-root': {
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  '&.Mui-selected': { bgcolor: 'rgba(139,195,74,0.24)' },
                  '&.Mui-selected:hover': { bgcolor: 'rgba(139,195,74,0.32)' },
                },
              }
            : {}),
        },
      },
    },
  };
}

function fieldSx(dark: boolean, menuZIndex?: number) {
  const selectMenuProps = buildSelectMenuProps(dark, menuZIndex);

  if (dark) {
    return {
      InputLabelProps: { sx: { color: 'rgba(255,255,255,0.75)' } },
      SelectProps: selectMenuProps,
      sx: {
        '& .MuiOutlinedInput-root': {
          color: '#fff',
          bgcolor: 'rgba(255,255,255,0.06)',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
          '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
        },
        '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.75)' },
      },
    };
  }

  if (selectMenuProps) {
    return { SelectProps: selectMenuProps };
  }

  return {};
}

function textFieldSx(dark: boolean) {
  return dark
    ? {
        InputLabelProps: { sx: { color: 'rgba(255,255,255,0.75)' } },
        sx: {
          '& .MuiOutlinedInput-root': {
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.06)',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
            '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
          },
        },
      }
    : {};
}

function DecisionRemarksField({
  value,
  onChange,
  compact = false,
  dark = false,
  dialogZIndex,
}: {
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  dark?: boolean;
  dialogZIndex?: number;
}) {
  const [pickOpen, setPickOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const tfs = textFieldSx(dark);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color={dark ? 'rgba(255,255,255,0.6)' : 'text.secondary'} fontWeight={700}>
          備註 Remarks
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setPickOpen(true)}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            ...(dark
              ? {
                  color: 'rgba(255,255,255,0.88)',
                  borderColor: 'rgba(255,255,255,0.28)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.45)', bgcolor: 'rgba(255,255,255,0.06)' },
                }
              : {}),
          }}
        >
          快速插入
        </Button>
      </Stack>
      <Box ref={anchorRef}>
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          rows={compact ? 2 : 2}
          placeholder="輸入備註…"
          {...tfs}
        />
      </Box>
      <RemarkQuickInsertDialog
        open={pickOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPickOpen(false)}
        dialogZIndex={dialogZIndex}
        dark={dark}
        onInsert={(labels) => {
          onChange(appendRemarkQuickInsert(value, labels));
          setPickOpen(false);
        }}
      />
    </>
  );
}

export default function DecisionFields({
  values,
  onChange,
  showJudgement = true,
  compact = false,
  dark = false,
  menuZIndex,
  dialogZIndex,
}: Props) {
  const fs = fieldSx(dark, menuZIndex);
  const hideReview = values.judgement === 'correct';

  const handleJudgement = (v: string) => {
    onChange('judgement', v);
    if (v === 'correct') {
      onChange('reviewed_decision', '');
      onChange('reviewed_punishment', '');
    } else {
      if (!values.reviewed_decision && values.on_field_decision) {
        onChange('reviewed_decision', values.on_field_decision);
      }
      if (!values.reviewed_punishment) {
        onChange('reviewed_punishment', values.on_field_punishment || 'no_penalty');
      }
    }
  };

  return (
    <Stack spacing={compact ? 1 : 1.5}>
      <Typography variant="caption" color={dark ? 'rgba(255,255,255,0.6)' : 'text.secondary'} fontWeight={700}>
        場上判決 On-field
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          select
          size="small"
          label="球權判決"
          value={values.on_field_decision}
          onChange={(e) => onChange('on_field_decision', e.target.value)}
          fullWidth
          {...fs}
        >
          <MenuItem value="">—</MenuItem>
          {ON_FIELD_BALL_DECISION_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {ON_FIELD_BALL_DECISION_LABELS[d]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="罰則"
          value={values.on_field_punishment}
          onChange={(e) => onChange('on_field_punishment', e.target.value)}
          fullWidth
          {...fs}
        >
          {ON_FIELD_PUNISHMENT_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {ON_FIELD_PUNISHMENT_LABELS[d]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {showJudgement && (
        <Box>
          <Typography variant="caption" color={dark ? 'rgba(255,255,255,0.6)' : 'text.secondary'} fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
            判決評價 Judgement
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            fullWidth
            value={values.judgement}
            onChange={(_, v) => v && handleJudgement(v)}
            sx={
              dark
                ? {
                    '& .MuiToggleButton-root': {
                      color: 'rgba(255,255,255,0.88)',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      borderColor: 'rgba(255,255,255,0.28)',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      '&[value="correct"] .MuiSvgIcon-root': {
                        color: getJudgementTextColor('correct', true),
                      },
                      '&[value="wrong"] .MuiSvgIcon-root': {
                        color: getJudgementTextColor('wrong', true),
                      },
                      '&[value="uncertain"] .MuiSvgIcon-root': {
                        color: getJudgementTextColor('uncertain', true),
                      },
                      '&[value="correct"].Mui-selected': {
                        color: '#c8e6c9',
                        bgcolor: 'rgba(46,125,50,0.42)',
                        borderColor: 'rgba(129,199,132,0.65)',
                        '&:hover': { bgcolor: 'rgba(46,125,50,0.48)' },
                      },
                      '&[value="wrong"].Mui-selected': {
                        color: '#ffcdd2',
                        bgcolor: 'rgba(198,40,40,0.42)',
                        borderColor: 'rgba(239,154,154,0.65)',
                        '&:hover': { bgcolor: 'rgba(198,40,40,0.48)' },
                      },
                      '&[value="uncertain"].Mui-selected': {
                        color: '#eceff1',
                        bgcolor: 'rgba(84,110,122,0.45)',
                        borderColor: 'rgba(176,190,197,0.55)',
                        '&:hover': { bgcolor: 'rgba(84,110,122,0.52)' },
                      },
                    },
                  }
                : {
                    '& .MuiToggleButton-root[value="correct"] .MuiSvgIcon-root': {
                      color: getJudgementTextColor('correct', false),
                    },
                    '& .MuiToggleButton-root[value="wrong"] .MuiSvgIcon-root': {
                      color: getJudgementTextColor('wrong', false),
                    },
                    '& .MuiToggleButton-root[value="uncertain"] .MuiSvgIcon-root': {
                      color: getJudgementTextColor('uncertain', false),
                    },
                    '& .MuiToggleButton-root[value="uncertain"].Mui-selected': {
                      color: JUDGEMENT_UNCERTAIN_COLOR,
                      bgcolor: 'rgba(84,110,122,0.12)',
                      borderColor: 'rgba(84,110,122,0.35)',
                    },
                  }
            }
          >
            {JUDGEMENT_OPTIONS.map((j) => (
              <ToggleButton
                key={j}
                value={j}
                color={dark ? undefined : j === 'correct' ? 'success' : j === 'wrong' ? 'error' : undefined}
                sx={
                  !dark && j === 'uncertain'
                    ? {
                        '&.Mui-selected': {
                          color: `${JUDGEMENT_UNCERTAIN_COLOR} !important`,
                          bgcolor: 'rgba(84,110,122,0.12) !important',
                          borderColor: 'rgba(84,110,122,0.35) !important',
                        },
                      }
                    : undefined
                }
              >
                {j === 'correct' && <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                {j === 'wrong' && <CancelIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                {j === 'uncertain' && <HelpOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                {JUDGEMENT_LABELS[j]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {!hideReview && (
        <>
          <Typography variant="caption" color={dark ? 'rgba(255,255,255,0.6)' : 'text.secondary'} fontWeight={700}>
            覆核判決 Reviewed
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="球權判決"
              value={values.reviewed_decision}
              onChange={(e) => onChange('reviewed_decision', e.target.value)}
              fullWidth
              {...fs}
            >
              <MenuItem value="">—</MenuItem>
              {REVIEWED_BALL_DECISION_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {REVIEWED_BALL_DECISION_LABELS[d]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="罰則"
              value={values.reviewed_punishment}
              onChange={(e) => onChange('reviewed_punishment', e.target.value)}
              fullWidth
              {...fs}
            >
              <MenuItem value="">—</MenuItem>
              {REVIEWED_PUNISHMENT_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {REVIEWED_PUNISHMENT_LABELS[d]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </>
      )}

      <DecisionRemarksField
        value={values.note}
        onChange={(v) => onChange('note', v)}
        compact={compact}
        dark={dark}
        dialogZIndex={dialogZIndex}
      />
    </Stack>
  );
}

export { textFieldSx };

export function JudgementChip({ judgement }: { judgement?: string | null }) {
  if (!judgement) return null;
  const normalized =
    judgement === 'correct'
      ? 'correct'
      : judgement === 'wrong' || judgement === 'incorrect'
        ? 'wrong'
        : judgement === 'uncertain'
          ? 'uncertain'
          : null;
  if (!normalized) {
    return (
      <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
        {judgement}
      </Typography>
    );
  }
  const color =
    normalized === 'correct' ? 'success.main' : normalized === 'wrong' ? 'error.main' : JUDGEMENT_UNCERTAIN_COLOR;
  return (
    <Typography component="span" variant="caption" sx={{ fontWeight: 700, color }}>
      {JUDGEMENT_LABELS[normalized]}
    </Typography>
  );
}

export function eventToDecisionForm(event?: {
  on_field_decision?: string | null;
  on_field_punishment?: string | null;
  reviewed_decision?: string | null;
  reviewed_punishment?: string | null;
  judgement?: string | null;
  note?: string | null;
  team_side?: string | null;
  event_type?: string;
} | null): DecisionFormValues {
  if (!event) return emptyDecisionForm();
  return {
    on_field_decision: event.on_field_decision || '',
    on_field_punishment: event.on_field_punishment || 'no_penalty',
    reviewed_decision: event.reviewed_decision || '',
    reviewed_punishment:
      event.judgement === 'correct' ? '' : event.reviewed_punishment || 'no_penalty',
    judgement: event.judgement || '',
    note: event.note || '',
    team_side: event.team_side || '',
    event_type: event.event_type || 'decision',
  };
}

export function normalizeDecisionPayload(values: DecisionFormValues) {
  const hideReview = values.judgement === 'correct';
  const needsReview = values.judgement === 'wrong' || values.judgement === 'uncertain';
  let reviewedDecision = hideReview ? null : values.reviewed_decision || null;
  let reviewedPunishment = hideReview ? null : values.reviewed_punishment || null;
  if (needsReview) {
    if (!reviewedDecision && values.on_field_decision) {
      reviewedDecision = values.on_field_decision;
    }
    if (!reviewedPunishment) {
      reviewedPunishment = values.on_field_punishment || 'no_penalty';
    }
  }
  return {
    on_field_decision: values.on_field_decision || null,
    on_field_punishment: values.on_field_punishment || 'no_penalty',
    reviewed_decision: reviewedDecision,
    reviewed_punishment: reviewedPunishment,
    judgement: values.judgement || null,
    note: values.note || null,
    team_side: null,
    event_type: values.event_type || 'decision',
  };
}

/** Allow saving when user has selected a judgement evaluation */
export function isDecisionMarkReady(values: DecisionFormValues): boolean {
  return values.judgement === 'correct' || values.judgement === 'wrong' || values.judgement === 'uncertain';
}
